import { useState, useRef, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import Avvvatars from "avvvatars-react";
import Icon1 from "@assets/icons/Icon";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import ChatPanel from "@components/chat/ChatPanel";
import FloatingChatWidget from "@components/chat/FloatingChatWidget";
import SearchField from "@components/ui/textfields/SearchField";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import DatePicker from "@components/ui/selection/DatePicker";
import MenuButton from "@components/ui/buttons/MenuButton";
import SideMenuButton from "@navigation/SideMenuButton";
import MenuInception from "@components/ui/buttons/MenuInception";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import BaseModal from "@components/modals/BaseModal";
import ConfirmationModal from "@components/support/ConfirmationModal";
import OrdreModal from "@components/modals/OrdreModal";
import { Text1 } from "@components/ui/texts/Texts";
import { getOrdreById, useOrdreStore } from "@store/ordreStore";
import { useAtelierStore } from "@store/atelierStore";
import { useDataStore } from "@store/user_org";
import { showNotification } from "context/notification.service";
import { navigateTo } from "context/navigation.service";
import { dossiersApi } from "@services/api/dossiers";
import { ordresApi } from "@services/api/ordres";
import type {
  Ordre,
  OrdreStatus,
  OrdreTag,
  OrdreIntervention,
  OrdreDocument,
  OrdreTimelineEvent,
  TrackingStep,
} from "types/ordre";
import type { Dossier } from "types/dossier";
import type { Contact } from "types/contact";
import type { ClientVehicle } from "types/client_vehicle";
import { TrackingContent } from "@screens/TrackingScreen";
import type { TrackingData } from "@services/api/tracking";
import { vehiclesApi } from "@services/api/vehicles";
import type { Vehicle } from "types/garage/vehicules";
import { interventionsApi, type Intervention } from "@services/api/interventions";
import PaymentRequestModal from "@components/modals/PaymentRequestModal";
import { paymentsApi } from "@services/api/payments";
import type { Payment } from "types/payment";

// ─── Constants ─────────────────────────────────────────────

const COLORIS_TO_PAINT: Record<string, string> = {
  noir: "black", blanc: "white", gris: "grey", bleu: "blue",
  rouge: "red", vert: "green", jaune: "yellow", marron: "brown",
  orange: "orange", autre: "silver",
};

function getVehicleImageUrl(marque?: string, modele?: string, coloris?: string): string | null {
  if (!marque || !modele) return null;
  const make = encodeURIComponent(marque.toLowerCase());
  const model = encodeURIComponent(modele.toLowerCase());
  const paint = COLORIS_TO_PAINT[coloris || ""] || "grey";
  return `https://cdn.imagin.studio/getimage?customer=img&make=${make}&modelFamily=${model}&modelYear=2023&zoomType=fullscreen&angle=0&paintDescription=${paint}`;
}

const STATUS_OPTIONS: { value: OrdreStatus; label: string; color: string }[] = [
  { value: "a_preparer", label: "À préparer", color: "#A1A1A1" },
  { value: "commande", label: "Commandé", color: "#8b5cf6" },
  { value: "pret", label: "Prêt", color: "#06b6d4" },
  { value: "reception", label: "Réception", color: "#f59e0b" },
  { value: "en_cours", label: "En cours", color: "#3b82f6" },
  { value: "devis", label: "Devis", color: "#f59e0b" },
  { value: "accord", label: "Accord", color: "#10b981" },
  { value: "fin_de_travaux", label: "Fin de travaux", color: "#10b981" },
  { value: "facturation", label: "Facturation", color: "#6366f1" },
  { value: "disponible", label: "Disponible", color: "#22c55e" },
  { value: "restitue", label: "Restitué", color: "#10b981" },
];

// Auto-mapping: when ordre status changes, tracking_step follows
const STATUS_TO_TRACKING: Partial<Record<OrdreStatus, TrackingStep>> = {
  a_preparer: "en_attente_depot",
  commande: "en_attente_depot",
  pret: "en_attente_depot",
  reception: "vehicule_depose",
  en_cours: "reparations_en_cours",
  devis: "devis_pret",
  accord: "reparations_en_cours",
  fin_de_travaux: "travaux_termines",
  facturation: "paiement_valide",
  disponible: "vehicule_disponible",
  restitue: "vehicule_restitue",
};

const TRACKING_STEP_OPTIONS: { value: TrackingStep; label: string }[] = [
  { value: "carte_grise_manquante", label: "Carte grise manquante" },
  { value: "acompte_manquant", label: "Acompte en attente" },
  { value: "en_attente_depot", label: "En attente du véhicule" },
  { value: "vehicule_depose", label: "Véhicule déposé" },
  { value: "devis_pret", label: "Devis prêt" },
  { value: "reparations_en_cours", label: "Réparations en cours" },
  { value: "paiement_valide", label: "Paiement validé" },
  { value: "travaux_termines", label: "Travaux terminés" },
  { value: "vehicule_disponible", label: "Véhicule disponible" },
  { value: "vehicule_restitue", label: "Véhicule restitué" },
];

const TAG_STYLES: Record<OrdreTag, { label: string; bg: string }> = {
  huile: { label: "HUILE", bg: "#F9A8D4" },
  diag: { label: "DIAG", bg: "#A78BFA" },
  pieces_en_attente: { label: "PIECES EN ATTENTE", bg: "#C4B5FD" },
  accidente: { label: "ACCIDENTE", bg: "#818CF8" },
  assurance: { label: "ASSURANCE", bg: "#A5B4FC" },
};

const CIVILITE_OPTIONS = [
  { value: "M.", label: "M." },
  { value: "Mme", label: "Mme" },
  { value: "Société", label: "Société" },
];

const PAYS_OPTIONS = [
  { value: "FRANCE", label: "FRANCE" },
  { value: "BELGIQUE", label: "BELGIQUE" },
  { value: "SUISSE", label: "SUISSE" },
  { value: "LUXEMBOURG", label: "LUXEMBOURG" },
];

const COLORIS_OPTIONS = [
  { value: "noir", label: "Noir" }, { value: "blanc", label: "Blanc" },
  { value: "gris", label: "Gris" }, { value: "bleu", label: "Bleu" },
  { value: "rouge", label: "Rouge" }, { value: "vert", label: "Vert" },
  { value: "autre", label: "Autre" },
];

const CONTENT_TABS = [
  { value: "fil", label: "Fil de l'ordre" },
  { value: "contacts", label: "Contacts" },
  { value: "interventions", label: "Interventions" },
  { value: "atelier", label: "Atelier" },
  { value: "vehicules", label: "Véhicules" },
  { value: "documents", label: "Documents" },
];

function formatTimestamp(ts?: { seconds: number }): string {
  if (!ts) return "—";
  return new Date(ts.seconds * 1000).toLocaleDateString("fr-FR", {
    day: "2-digit", month: "2-digit",
  }) + " " + new Date(ts.seconds * 1000).toLocaleTimeString("fr-FR", {
    hour: "2-digit", minute: "2-digit",
  });
}

function formatHour(h: number): string {
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return `${hrs}h${mins.toString().padStart(2, "0")}`;
}

// ─── Timeline component ────────────────────────────────────

function formatEventDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) + " " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

const IMAGE_NAME_RE = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i;

function TimelineDocThumbnail({ docName, documents, ordreId }: { docName: string; documents: OrdreDocument[]; ordreId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const doc = documents.find((d) => d.name === docName);
    if (!doc) return;
    let cancelled = false;
    ordresApi.getDocumentUrl(ordreId, doc.id).then((u) => { if (!cancelled) setUrl(u); }).catch(() => {});
    return () => { cancelled = true; };
  }, [docName, documents, ordreId]);

  return (
    <div className="w-[80px] h-[80px] rounded-lg overflow-hidden bg-neutral-100 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0">
      {url ? (
        <img src={url} alt={docName} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Icon1 name="image" size={24} color="#D4D4D4" />
        </div>
      )}
    </div>
  );
}

function TimelineEvent({ event, isLast, documents, ordreId }: { event: OrdreTimelineEvent; isLast: boolean; documents?: OrdreDocument[]; ordreId?: string }) {
  const imageNames = (event.type === "documents" && event.document_names)
    ? event.document_names.filter((n) => IMAGE_NAME_RE.test(n))
    : [];
  const fileNames = (event.type === "documents" && event.document_names)
    ? event.document_names.filter((n) => !IMAGE_NAME_RE.test(n))
    : [];

  return (
    <div className="flex flex-row">
      <div className="flex-shrink-0 text-h3 text-neutral-400" style={{ width: 90, paddingTop: 2, textAlign: "right", paddingRight: 12 }}>
        {formatEventDate(event.date)}
      </div>
      <div className="flex flex-col items-center flex-shrink-0" style={{ width: 20 }}>
        <div className="rounded-full mt-1.5 flex-shrink-0" style={{ width: 10, height: 10, background: "#A3A3A3" }} />
        {!isLast && <div className="w-0.5 bg-neutral-300 flex-1 my-1" />}
      </div>
      <div className="flex flex-col gap-2 ml-3 flex-1 pb-6" style={{ minWidth: 0 }}>
        <div className="flex flex-row gap-2 items-start">
          <div className="flex-shrink-0 overflow-hidden" style={{ borderRadius: 999 }}>
            <Avvvatars value={event.user_name} style="shape" size={32} />
          </div>
          <div className="flex flex-col">
            <span className="text-h2 font-semibold" style={{ lineHeight: "18px" }}>{event.user_name}</span>
            <span className="text-h3 text-neutral-400">{event.action}</span>
          </div>
        </div>
        {event.type === "documents" && imageNames.length > 0 && documents && ordreId && (
          <div className="flex flex-row gap-2 mt-1 flex-wrap">
            {imageNames.map((name, i) => (
              <TimelineDocThumbnail key={i} docName={name} documents={documents} ordreId={ordreId} />
            ))}
          </div>
        )}
        {event.type === "documents" && fileNames.length > 0 && (
          <div className="flex flex-col gap-0.5 mt-1">
            {fileNames.map((name, i) => (
              <div key={i} className="flex flex-row items-center gap-2 cursor-pointer hover:bg-neutral-50 rounded px-1 py-0.5">
                <Icon1 name="file" size={14} color="#A1A1A1" />
                <span className="text-h3 text-neutral-600">{name}</span>
              </div>
            ))}
          </div>
        )}
        {event.type === "photos" && event.photo_urls && (
          <div className="flex flex-row gap-2 mt-1 flex-wrap">
            {event.photo_urls.map((url, i) => (
              <div key={i} className="w-[80px] h-[80px] rounded-lg overflow-hidden bg-neutral-100 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0">
                <img src={url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        )}
        {event.type === "status_change" && event.status_label && (
          <div className="flex flex-row items-center gap-2 mt-1">
            <span className="text-h3 font-medium" style={{ color: event.status_type === "success" ? "#10b981" : event.status_type === "warning" ? "#f59e0b" : "#6366f1" }}>
              {event.status_label}
            </span>
            <Icon1 name={event.status_type === "success" ? "done" : "horloge"} size={16} color={event.status_type === "success" ? "#10b981" : "#6366f1"} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Fil de l'ordre tab ─────────────────────────────────────

function FilTab({ timeline, search, documents, ordreId }: { timeline: OrdreTimelineEvent[]; search: string; documents?: OrdreDocument[]; ordreId?: string }) {
  const filtered = search.trim()
    ? timeline.filter((ev) => {
        const q = search.toLowerCase();
        return ev.user_name.toLowerCase().includes(q) || ev.action.toLowerCase().includes(q) || (ev.document_names || []).some((n) => n.toLowerCase().includes(q)) || (ev.status_label || "").toLowerCase().includes(q);
      })
    : timeline;
  const sorted = [...filtered].sort((a, b) => (a.date < b.date ? 1 : -1));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col">
        {sorted.length === 0 ? (
          <div className="text-h3 text-center text-neutral-400 py-8">{search ? "Aucun résultat" : "Aucun événement"}</div>
        ) : sorted.map((ev, i) => <TimelineEvent key={ev.id} event={ev} isLast={i === sorted.length - 1} documents={documents} ordreId={ordreId} />)}
      </div>
    </div>
  );
}

// ─── Contacts tab (editable form) ──────────────────────────

function ContactsTab({ contacts: initial, ordreId }: { contacts: Contact[]; ordreId: string }) {
  const [contacts, setContacts] = useState<Contact[]>(initial);
  const [saving, setSaving] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const saveContacts = (updated: Contact[]) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        await ordresApi.update(ordreId, { contacts: updated } as any);
      } catch {
        showNotification({ type: "error", message: "Erreur lors de la sauvegarde" });
      } finally {
        setSaving(false);
      }
    }, 800);
  };

  const updateContact = (id: string, field: keyof Contact, value: string) => {
    setContacts((prev) => {
      const updated = prev.map((c) => (c.id === id ? { ...c, [field]: value } : c));
      saveContacts(updated);
      return updated;
    });
  };

  const addContact = () => {
    setContacts((prev) => {
      const updated: Contact[] = [...prev, { id: `c-new-${Date.now()}`, dossier_id: "", nom: "", prenom: "", civilite: "M." as const, pays: "FRANCE", phone: "", email: "", is_primary: false, created_at: new Date().toISOString() }];
      saveContacts(updated);
      return updated;
    });
  };

  return (
    <div className="flex flex-col gap-6">
      {contacts.map((c, idx) => (
        <div key={c.id} className="flex flex-col gap-3 border border-neutral-200 rounded-xl p-4">
          <div className="flex flex-row items-center justify-between">
            <span className="text-h3 font-medium text-neutral-500">Contact {idx + 1}</span>
            {saving && idx === 0 && <span className="text-h4 text-neutral-400">Sauvegarde...</span>}
          </div>
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <TextField label="Nom" value={c.nom} onChange={(v) => updateContact(c.id, "nom", v)} />
            </div>
            <div style={{ width: 140 }}>
              <DropDown label="Civilité" options={CIVILITE_OPTIONS} value={c.civilite || ""} onSelect={(v) => updateContact(c.id, "civilite", String(v))} />
            </div>
          </div>
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <TextField label="Prénom" value={c.prenom || ""} onChange={(v) => updateContact(c.id, "prenom", v)} />
            </div>
            <div className="flex-1">
              <DropDown label="Pays" options={PAYS_OPTIONS} value={c.pays || ""} onSelect={(v) => updateContact(c.id, "pays", String(v))} />
            </div>
          </div>
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <TextField label="Téléphone" value={c.phone || ""} onChange={(v) => updateContact(c.id, "phone", v)} />
            </div>
            <div className="flex-1">
              <TextField label="Email" value={c.email || ""} onChange={(v) => updateContact(c.id, "email", v)} />
            </div>
          </div>
        </div>
      ))}
      <button
        className="flex flex-row items-center gap-2 text-h3 font-medium text-neutral-600 border border-neutral-200 rounded-lg px-4 py-2 w-fit hover:bg-neutral-50"
        onClick={addContact}
      >
        <Icon1 name="plus" size={14} color="#525252" />
        Ajouter un contact
      </button>
    </div>
  );
}

// ─── Interventions tab ──────────────────────────────────────

function InterventionsTab({ interventions: initial, configuredInterventions }: { interventions: OrdreIntervention[]; configuredInterventions: Intervention[] }) {
  const [interventions, setInterventions] = useState(initial);
  const [showPicker, setShowPicker] = useState(false);

  const addFromCatalog = (catalog: Intervention) => {
    const hours = +(catalog.estimated_minutes / 60).toFixed(2);
    setInterventions((prev) => [...prev, {
      id: `i-new-${Date.now()}`,
      label: catalog.name,
      default_hours: hours,
      estimated_hours: hours,
      assigned_to: "",
    }]);
    setShowPicker(false);
  };

  const addBlank = () => {
    setInterventions((prev) => [...prev, {
      id: `i-new-${Date.now()}`,
      label: "",
      default_hours: 0,
      estimated_hours: 0,
      assigned_to: "",
    }]);
    setShowPicker(false);
  };

  const removeIntervention = (id: string) => {
    setInterventions((prev) => prev.filter((i) => i.id !== id));
  };

  // Group configured interventions by category
  const CATEGORY_LABELS: Record<string, string> = {
    mecanique: "Mécanique",
    carrosserie: "Carrosserie",
    vitrage: "Vitrage",
    pneumatique: "Pneumatique",
  };

  return (
    <div className="flex flex-col gap-4">
      <span className="text-h3 font-medium text-neutral-500">
        Interventions prévues ({interventions.length})
      </span>

      {interventions.map((inter) => (
        <div key={inter.id} className="flex flex-row items-center gap-3 py-3 border-b border-neutral-100">
          <div className="flex-1 min-w-0">
            <span className="text-h3 font-medium">{inter.label || "Nouvelle intervention"}</span>
          </div>
          <span className="text-h3 text-neutral-500" style={{ width: 50, textAlign: "right" }}>{inter.default_hours}h</span>
          <span className="text-h3 font-medium" style={{ width: 50, textAlign: "right" }}>{inter.estimated_hours}h</span>
          <button
            className="text-h4 text-neutral-400 hover:text-red-500 transition-colors px-1"
            onClick={() => removeIntervention(inter.id)}
            title="Retirer"
          >
            <Icon1 name="close" size={12} />
          </button>
        </div>
      ))}

      {interventions.length === 0 && (
        <div className="text-h3 text-center text-neutral-400 py-8">Aucune intervention</div>
      )}

      <div className="relative">
        <button
          className="flex flex-row items-center gap-2 text-h3 font-medium text-neutral-600 border border-neutral-200 rounded-lg px-4 py-2 w-fit hover:bg-neutral-50"
          onClick={() => setShowPicker(!showPicker)}
        >
          <Icon1 name="plus" size={14} color="#525252" />
          Ajouter une intervention
        </button>

        {showPicker && (
          <div
            className="absolute z-50 mt-1 bg-white rounded-lg border border-neutral-200 shadow-lg overflow-y-auto"
            style={{ maxHeight: 320, width: 340 }}
          >
            {Object.entries(CATEGORY_LABELS).map(([cat, label]) => {
              const items = configuredInterventions.filter((i) => i.category === cat && i.active);
              if (items.length === 0) return null;
              return (
                <div key={cat}>
                  <div className="px-3 py-1.5 text-h4 font-semibold text-neutral-400 bg-neutral-50 sticky top-0">
                    {label}
                  </div>
                  {items.map((item) => {
                    const h = Math.floor(item.estimated_minutes / 60);
                    const m = item.estimated_minutes % 60;
                    const dur = h > 0 && m > 0 ? `${h}h${m.toString().padStart(2, "0")}` : h > 0 ? `${h}h` : `${m}min`;
                    return (
                      <button
                        key={item.id}
                        className="flex flex-row items-center justify-between w-full px-3 py-2 hover:bg-indigo-50 transition-colors text-left"
                        onClick={() => addFromCatalog(item)}
                      >
                        <span className="text-h3 text-neutral-600">{item.name}</span>
                        <span className="text-h4 text-neutral-400 ml-2">{dur}</span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
            <div className="border-t border-neutral-100">
              <button
                className="flex flex-row items-center gap-2 w-full px-3 py-2 hover:bg-neutral-50 transition-colors text-left"
                onClick={addBlank}
              >
                <Icon1 name="plus" size={12} color="#A1A1A1" />
                <span className="text-h3 text-neutral-400">Intervention personnalisée</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Atelier tab ────────────────────────────────────────────

function AtelierTab({ charges }: { charges: { id: string; worker_name: string; specialty: string; date: string; start_hour: number; duration_hours: number }[] }) {
  return (
    <div className="flex flex-col gap-4">
      <span className="text-h3 font-medium text-neutral-500">
        Charges Atelier ({charges.length})
      </span>

      {charges.map((c) => (
        <div key={c.id} className="flex flex-col gap-2 py-3 border-b border-neutral-100">
          <div className="flex flex-row items-center justify-between">
            <span className="text-h3 font-semibold">
              {c.date} {formatHour(c.start_hour)} — {formatHour(c.start_hour + c.duration_hours)}
            </span>
          </div>
          <div className="text-h4 text-neutral-500 leading-relaxed" style={{ maxWidth: 500 }}>
            Intervention mécanique générale
          </div>
          <div className="flex flex-row items-center gap-3 mt-1">
            <span className="text-h3 font-medium">{c.worker_name}</span>
            <span className="text-h3 text-neutral-500">{c.duration_hours} h</span>
            <span className="text-h4 text-neutral-500">MECA</span>
            <span
              className="text-h4 font-medium px-3 py-1 rounded-full"
              style={{ background: "#E0E7FF", color: "#3730A3" }}
            >
              Ventilée
            </span>
          </div>
        </div>
      ))}

      {charges.length === 0 && (
        <div className="text-h3 text-center text-neutral-400 py-8">Aucune charge atelier</div>
      )}
    </div>
  );
}

// ─── Véhicules tab (selectable list from dossier + detail) ──

function VehiculesTab({
  dossierVehicles,
  selectedVehicleId,
  onSelectVehicle,
}: {
  dossierVehicles: ClientVehicle[];
  selectedVehicleId?: string;
  onSelectVehicle: (vehicleId: string) => void;
}) {
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const selectedVehicle = dossierVehicles.find((v) => v.id === selectedVehicleId);

  return (
    <div className="flex flex-col gap-4">
      {/* Vehicle selection list */}
      {dossierVehicles.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-h3 font-semibold text-neutral-500">Véhicules du dossier</span>
          {dossierVehicles.map((v) => {
            const isSelected = v.id === selectedVehicleId;
            const imgUrl = getVehicleImageUrl(v.marque, v.modele, v.coloris);
            return (
              <button
                key={v.id}
                className="flex flex-row items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors"
                style={{
                  borderColor: isSelected ? "#5C6BFF" : "#E5E5E5",
                  background: isSelected ? "#F5F6FF" : "#FFFFFF",
                }}
                onClick={() => onSelectVehicle(v.id)}
              >
                {/* Checkbox */}
                <div
                  className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                  style={{
                    borderColor: isSelected ? "#5C6BFF" : "#D4D4D4",
                    background: isSelected ? "#5C6BFF" : "transparent",
                  }}
                >
                  {isSelected && (
                    <svg width={10} height={8} viewBox="0 0 10 8" fill="none">
                      <path d="M1 4l3 3 5-6" stroke="#FFFFFF" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>

                {/* Vehicle thumbnail */}
                <div className="w-16 h-10 rounded-lg bg-neutral-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {imgUrl && !imageErrors[v.id] ? (
                    <img
                      src={imgUrl}
                      alt={v.plate}
                      className="w-full h-full object-contain"
                      onError={() => setImageErrors((prev) => ({ ...prev, [v.id]: true }))}
                    />
                  ) : (
                    <Icon1 name="image" size={16} color="#D4D4D4" />
                  )}
                </div>

                {/* Vehicle info */}
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="text-h3 font-medium truncate">
                    {v.marque} {v.modele}
                  </span>
                  <span className="text-h4 text-neutral-400">{v.plate}{v.km ? ` · ${v.km.toLocaleString()} km` : ""}</span>
                </div>

                {isSelected && (
                  <span className="text-h4 font-medium flex-shrink-0" style={{ color: "#5C6BFF" }}>Sélectionné</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Selected vehicle detail card */}
      {selectedVehicle && (
        <div className="flex flex-col gap-3 mt-2">
          <span className="text-h3 font-semibold text-neutral-500">Détail du véhicule sélectionné</span>
          <div className="w-full rounded-xl border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden" style={{ height: 180 }}>
            {(() => {
              const imgUrl = getVehicleImageUrl(selectedVehicle.marque, selectedVehicle.modele, selectedVehicle.coloris);
              return imgUrl && !imageErrors[selectedVehicle.id] ? (
                <img src={imgUrl} alt={selectedVehicle.plate} className="w-full h-full object-contain" onError={() => setImageErrors((prev) => ({ ...prev, [selectedVehicle.id]: true }))} />
              ) : (
                <div className="flex flex-col items-center gap-2 text-neutral-400">
                  <Icon1 name="image" size={40} color="#D4D4D4" />
                  <span className="text-h3 font-medium">Image non disponible</span>
                </div>
              );
            })()}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <TextField label="Marque" value={selectedVehicle.marque || ""} onChange={() => {}} />
            <TextField label="Modèle" value={selectedVehicle.modele || ""} onChange={() => {}} />
            <TextField label="Immatriculation" value={selectedVehicle.plate || ""} onChange={() => {}} />
            <TextField label="Km" value={String(selectedVehicle.km || "")} onChange={() => {}} />
            <TextField label="VIN" value={selectedVehicle.vin || ""} onChange={() => {}} />
            <DropDown label="Coloris" options={COLORIS_OPTIONS} value={selectedVehicle.coloris || ""} onSelect={() => {}} />
          </div>
        </div>
      )}

      {dossierVehicles.length === 0 && (
        <div className="text-h3 text-center text-neutral-400 py-8">Aucun véhicule dans le dossier</div>
      )}
    </div>
  );
}

// ─── Documents tab ──────────────────────────────────────────

const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i;

function isImageDoc(d: { type: string; name: string; gcs_path?: string }) {
  return d.type === "image" || IMAGE_EXTENSIONS.test(d.gcs_path || d.name);
}

function PhotoThumbnail({ doc, onClick }: { doc: { id: string; name: string; gcs_path?: string }; onClick: () => void }) {
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!doc.gcs_path) return;
    const isOrdreDoc = doc.gcs_path.startsWith("ordres/");
    const fetchUrl = async () => {
      try {
        // Extract IDs from gcs_path: ordres/{org}/{ordreId}/... or dossiers/{org}/{dossierId}/...
        const parts = doc.gcs_path!.split("/");
        const parentId = parts[2]; // ordreId or dossierId
        const url = isOrdreDoc
          ? await ordresApi.getDocumentUrl(parentId, doc.id)
          : await dossiersApi.getDocumentUrl(parentId, doc.id);
        if (!cancelled) setThumbUrl(url);
      } catch { /* silently fail */ }
    };
    fetchUrl();
    return () => { cancelled = true; };
  }, [doc.gcs_path, doc.id]);

  return (
    <div
      className="w-[120px] h-[120px] rounded-lg overflow-hidden bg-neutral-100 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
      onClick={onClick}
    >
      {thumbUrl ? (
        <img src={thumbUrl} alt={doc.name} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <Icon1 name="image" size={24} color="#D4D4D4" />
      )}
    </div>
  );
}

function DocumentsTab({
  documents,
  dossierId,
  ordreId,
  onPreview,
  onAddDoc,
  onSignRequest,
  devisSignStatus,
  devisDocumentId,
}: {
  documents: Array<{ id: string; name: string; type: string; created_at?: string; gcs_path?: string }>;
  dossierId?: string;
  ordreId?: string;
  onPreview: (doc: any) => void;
  onAddDoc: () => void;
  onSignRequest: (doc: { id: string; name: string }) => void;
  devisSignStatus?: string;
  devisDocumentId?: string;
}) {
  const photos = documents.filter(isImageDoc);
  const files = documents.filter((d) => !isImageDoc(d));

  const isPdf = (d: { type: string; name: string; gcs_path?: string }) =>
    d.type === "pdf" || /\.pdf$/i.test(d.gcs_path || d.name);

  return (
    <div className="flex flex-col gap-4">
      {/* Photo gallery */}
      {photos.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-h3 font-medium text-neutral-500">Photos ({photos.length})</span>
          <div className="flex flex-row flex-wrap gap-2">
            {photos.map((p) => (
              <PhotoThumbnail key={p.id} doc={p} onClick={() => onPreview(p)} />
            ))}
          </div>
        </div>
      )}

      {/* Files list */}
      {files.length > 0 && (
        <div className="flex flex-col gap-2">
          {photos.length > 0 && <span className="text-h3 font-medium text-neutral-500">Documents ({files.length})</span>}
          {files.map((d) => {
            const isDevisDoc = d.id === devisDocumentId;
            const signedAlready = isDevisDoc && devisSignStatus === "signed";
            return (
              <div key={d.id} className="flex flex-row items-center gap-3 py-2 hover:bg-neutral-50 rounded-lg px-2">
                <div
                  className="flex flex-row items-center gap-3 flex-1 min-w-0 cursor-pointer"
                  onClick={() => onPreview(d)}
                >
                  <div className="w-28 h-28 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0">
                    <Icon1 name={d.type === "pdf" ? "file" : "image"} size={16} color="#A1A1A1" />
                  </div>
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-h3 font-medium truncate">{d.name}</span>
                    {d.created_at && <span className="text-h4 text-neutral-400">{d.created_at}</span>}
                  </div>
                </div>
                <div className="flex flex-row items-center gap-2 flex-shrink-0">
                  {isDevisDoc && devisSignStatus === "pending" && (
                    <span className="text-h4 font-medium px-2 py-0.5 rounded-full" style={{ background: "#DBEAFE", color: "#1E40AF" }}>
                      En attente de signature
                    </span>
                  )}
                  {signedAlready && (
                    <span className="text-h4 font-medium px-2 py-0.5 rounded-full" style={{ background: "#D1FAE5", color: "#065F46" }}>
                      Signé
                    </span>
                  )}
                  {isPdf(d) && !signedAlready && !d.name.includes("(signé)") && (
                    <button
                      className="text-h4 font-medium px-3 py-1 rounded-lg border border-neutral-200 hover:bg-indigo-50 hover:border-indigo-200 transition-colors"
                      style={{ color: "#4F46E5" }}
                      onClick={(e) => { e.stopPropagation(); onSignRequest(d); }}
                    >
                      Envoyer pour signature
                    </button>
                  )}
                  <button onClick={() => onPreview(d)} className="p-1 hover:bg-neutral-100 rounded">
                    <Icon1 name="download" size={16} color="#A1A1A1" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {documents.length === 0 && (
        <div className="text-h3 text-center text-neutral-400 py-8">Aucun document</div>
      )}
      <button
        className="flex flex-row items-center gap-2 text-h3 font-medium text-neutral-600 border border-neutral-200 rounded-lg px-4 py-2 w-fit hover:bg-neutral-50 mt-2"
        onClick={onAddDoc}
      >
        <Icon1 name="plus" size={14} color="#525252" />
        Ajouter un document
      </button>
    </div>
  );
}

// ─── Document preview modal ─────────────────────────────────

function DocumentPreviewModal({
  doc,
  url,
  onClose,
  onDownload,
}: {
  doc: { name: string; gcs_path?: string };
  url: string;
  onClose: () => void;
  onDownload: () => void;
}) {
  const nameOrPath = doc.gcs_path || doc.name;
  const isImage = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i.test(nameOrPath);
  const isPdf = /\.pdf$/i.test(nameOrPath);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ width: "80vw", maxWidth: 900, height: "85vh" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-row items-center justify-between px-5 py-3 border-b border-neutral-200">
          <span className="text-h2 font-semibold">{doc.name}</span>
          <div className="flex flex-row items-center gap-2">
            <button onClick={onDownload} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-200 hover:bg-neutral-50 text-h3 font-medium text-neutral-600">
              <Icon1 name="download" size={14} color="#525252" />
              Télécharger
            </button>
            <button onClick={onClose} className="flex items-center justify-center rounded-lg hover:bg-neutral-100" style={{ width: 32, height: 32 }}>
              <Icon1 name="close" size={16} color="#525252" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto flex items-center justify-center bg-neutral-50">
          {isPdf ? (
            <iframe src={url} className="w-full h-full" style={{ border: "none" }} title={doc.name} />
          ) : isImage ? (
            <img src={url} alt={doc.name} className="max-w-full max-h-full object-contain" />
          ) : (
            <div className="flex flex-col items-center gap-3 text-neutral-400">
              <Icon1 name="file" size={48} color="#D4D4D4" />
              <span className="text-h2 font-medium">Aperçu non disponible</span>
              <button onClick={onDownload} className="px-4 py-2 rounded-lg bg-indigo-500 text-white text-h3 font-medium hover:bg-indigo-600">Télécharger le fichier</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Add document modal (uploads to dossier) ────────────────

const DOCUMENT_TYPE_OPTIONS = [
  { value: "carte_grise", label: "Carte grise" },
  { value: "assurance", label: "Assurance" },
  { value: "controle_technique", label: "Contrôle technique" },
  { value: "constat_amiable", label: "Constat amiable" },
  { value: "permis_conduire", label: "Permis de conduire" },
  { value: "devis", label: "Devis" },
  { value: "facture", label: "Facture" },
  { value: "bon_commande", label: "Bon de commande" },
  { value: "bon_livraison", label: "Bon de livraison" },
  { value: "contrat_location", label: "Contrat de location / prêt" },
  { value: "rapport_expertise", label: "Rapport d'expertise" },
  { value: "photo_vehicule", label: "Photo véhicule" },
  { value: "photo_degats", label: "Photo dégâts" },
  { value: "courrier", label: "Courrier" },
  { value: "autre", label: "Autre" },
];

function AddDocumentModalOrdre({
  dossierId,
  onClose,
  onSuccess,
}: {
  dossierId: string;
  onClose: () => void;
  onSuccess: (dossier: Dossier) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [docName, setDocName] = useState("");
  const [docType, setDocType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setDocName(selectedFile.name);
    setClassifying(true);
    try {
      const result = await dossiersApi.classifyDocument(selectedFile);
      if (result.suggested_name) {
        const origExt = selectedFile.name.includes(".") ? selectedFile.name.substring(selectedFile.name.lastIndexOf(".")) : "";
        const suggestedHasExt = result.suggested_name.includes(".");
        setDocName(suggestedHasExt ? result.suggested_name : result.suggested_name + origExt);
      }
      if (result.type_key) setDocType(result.type_key);
    } catch { /* user can manually select */ }
    finally { setClassifying(false); }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const updated = await dossiersApi.uploadDocument(dossierId, file, docName || undefined, docType || undefined);
      showNotification({ type: "success", message: "Document ajouté" });
      onSuccess(updated);
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'envoi" });
    } finally { setUploading(false); }
  };

  return (
    <BaseModal title="Ajouter un document" startIcon={<Icon1 name="upload" size={20} />} onClose={onClose} width="max-w-md">
      <div className="flex flex-col gap-4 p-4">
        <div
          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${dragOver ? "border-indigo-400 bg-indigo-50" : "border-neutral-300 hover:bg-neutral-50"}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleFileSelect(f); }}
        >
          {file ? (
            <div className="flex flex-col items-center gap-2">
              <Icon1 name="file" size={28} color="#6366f1" />
              <span className="text-h3 font-medium text-neutral-700 text-center">{file.name}</span>
              <span className="text-h4 text-neutral-400">{(file.size / 1024).toFixed(0)} Ko</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Icon1 name="upload" size={24} color="#A1A1A1" />
              <span className="text-h3 text-neutral-400">Glisser ou cliquer pour importer</span>
              <span className="text-h4 text-neutral-300">PDF, images, documents</span>
            </div>
          )}
          <input ref={fileInputRef} type="file" className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }} />
        </div>
        {classifying && (
          <div className="flex flex-row items-center gap-2 p-3 rounded-lg bg-indigo-50 border border-indigo-100">
            <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-h3 text-indigo-600">Analyse du document...</span>
          </div>
        )}
        <TextField label="Nom du document" value={docName} onChange={setDocName} />
        <DropDown label="Type de document" options={DOCUMENT_TYPE_OPTIONS} value={docType} onSelect={(v) => setDocType(String(v))} />
      </div>
      <div className="border-t p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>Annuler</SecondaryButton>
        <Button variant="small" label={uploading ? "Envoi..." : "Ajouter"} onClick={handleUpload} disabled={!file || uploading} />
      </div>
    </BaseModal>
  );
}

// ─── Status dropdown ────────────────────────────────────────

const STATUS_CHIP_STYLES: Record<string, { label: string; bg: string; color: string }> = {
  a_preparer: { label: "en prépa", bg: "#F4F4F4", color: "#A1A1A1" },
  commande: { label: "commande", bg: "#F4F4F4", color: "#A1A1A1" },
  pret: { label: "prêt", bg: "#F4F4F4", color: "#A1A1A1" },
  reception: { label: "réception", bg: "#F4F4F4", color: "#A1A1A1" },
  en_cours: { label: "en cours", bg: "#CACFFF", color: "#5C6BFF" },
  devis: { label: "devis", bg: "#F4F4F4", color: "#A1A1A1" },
  accord: { label: "accord", bg: "#F4F4F4", color: "#A1A1A1" },
  fin_de_travaux: { label: "fin de travaux", bg: "#F4F4F4", color: "#A1A1A1" },
  facturation: { label: "facturation", bg: "#F4F4F4", color: "#A1A1A1" },
  disponible: { label: "disponible", bg: "#F1E7F9", color: "#8258FF" },
  restitue: { label: "restitué", bg: "#E3FEFC", color: "#5EEAD4" },
};

function OrdreStatusDropdown({
  currentStatus,
  onStatusChange,
}: {
  currentStatus: OrdreStatus;
  onStatusChange: (status: OrdreStatus) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const currentIdx = STATUS_OPTIONS.findIndex((s) => s.value === currentStatus);
  const chip = STATUS_CHIP_STYLES[currentStatus] || { label: currentStatus, bg: "#F4F4F4", color: "#A1A1A1" };

  // Reorder: next logical status on top, then the rest (current excluded)
  const reorderedOptions = useMemo(() => {
    const others = STATUS_OPTIONS.filter((s) => s.value !== currentStatus);
    const nextIdx = currentIdx + 1;
    if (nextIdx >= STATUS_OPTIONS.length) return others;
    const nextValue = STATUS_OPTIONS[nextIdx].value;
    const nextItem = others.find((s) => s.value === nextValue);
    const rest = others.filter((s) => s.value !== nextValue);
    return nextItem ? [nextItem, ...rest] : others;
  }, [currentStatus, currentIdx]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex flex-row items-center gap-1.5 text-h3 font-medium cursor-pointer transition-opacity hover:opacity-80"
        style={{ background: chip.bg, color: chip.color, borderRadius: 5, padding: "4px 14px", lineHeight: "18px" }}
      >
        {chip.label}
        <svg width={8} height={5} viewBox="0 0 8 5" fill="none">
          <path d="M1 1l3 3 3-3" stroke={chip.color} strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-1 z-50 bg-white rounded-xl shadow-lg border border-neutral-200 overflow-hidden"
          style={{ minWidth: 190 }}
        >
          {reorderedOptions.map((opt, i) => {
            const optChip = STATUS_CHIP_STYLES[opt.value] || { label: opt.value, bg: "#F4F4F4", color: "#A1A1A1" };
            const isNext = i === 0 && currentIdx + 1 < STATUS_OPTIONS.length && opt.value === STATUS_OPTIONS[currentIdx + 1].value;
            return (
              <button
                key={opt.value}
                className="flex flex-row items-center gap-2.5 w-full px-3 py-2 hover:bg-neutral-50 transition-colors text-left"
                style={{ borderBottom: i === 0 && isNext ? "1px solid #E5E5E5" : undefined }}
                onClick={() => { onStatusChange(opt.value); setOpen(false); }}
              >
                <span
                  className="text-h3 font-medium whitespace-nowrap"
                  style={{ background: optChip.bg, color: optChip.color, borderRadius: 5, padding: "2px 10px", lineHeight: "18px" }}
                >
                  {optChip.label}
                </span>
                {isNext && (
                  <span className="text-h4 text-neutral-400 ml-auto">suivant</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Assign dropdown ──────────────────────────────────────

function AssignDropdown({
  assignedTo,
  onAssign,
}: {
  assignedTo?: string;
  onAssign: (userId: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const orgMembers = useDataStore((s) => s.orgMembers);
  const assignedMember = orgMembers.find((m) => m.id === assignedTo);
  const assignedName = assignedMember ? (assignedMember.infos.display_name || assignedMember.infos.full_name) : assignedTo;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex flex-row items-center gap-1.5 text-h3 font-medium cursor-pointer transition-opacity hover:opacity-80 rounded-full px-3 py-1"
        style={{
          background: assignedTo ? "#E0E7FF" : "#F4F4F4",
          color: assignedTo ? "#3730A3" : "#A1A1A1",
        }}
      >
        <Icon1 name="user" size={12} color={assignedTo ? "#3730A3" : "#A1A1A1"} />
        {assignedName || "Non assigné"}
        <svg width={8} height={5} viewBox="0 0 8 5" fill="none">
          <path d="M1 1l3 3 3-3" stroke={assignedTo ? "#3730A3" : "#A1A1A1"} strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-1 z-50 bg-white rounded-xl shadow-lg border border-neutral-200 overflow-hidden"
          style={{ minWidth: 220 }}
        >
          {/* Unassign option */}
          {assignedTo && (
            <button
              className="flex flex-row items-center gap-2.5 w-full px-3 py-2 hover:bg-neutral-50 transition-colors text-left border-b border-neutral-100"
              onClick={() => { onAssign(null); setOpen(false); }}
            >
              <Icon1 name="close" size={14} color="#A1A1A1" />
              <span className="text-h3 text-neutral-400">Retirer l'assignation</span>
            </button>
          )}
          {orgMembers.map((member) => {
            const name = member.infos.display_name || member.infos.full_name;
            const isSelected = assignedTo === member.id;
            return (
              <button
                key={member.id}
                className="flex flex-row items-center gap-2.5 w-full px-3 py-2 hover:bg-neutral-50 transition-colors text-left"
                onClick={() => { onAssign(member.id); setOpen(false); }}
              >
                <div className="flex-shrink-0" style={{ borderRadius: 999 }}>
                  <Avvvatars value={name} style="shape" size={24} />
                </div>
                <span className="text-h3 font-medium flex-1 truncate">{name}</span>
                {isSelected && <Icon1 name="done2" size={16} color="#5C6BFF" />}
              </button>
            );
          })}
          {orgMembers.length === 0 && (
            <div className="px-3 py-3 text-h3 text-neutral-400 text-center">Aucun membre</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Rental vehicle badge with popover ──────────────────────

function RentalVehicleBadge({ vehicle, reservationId }: { vehicle: Vehicle; reservationId?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const usageLabel = vehicle.usage === "velos" ? "Vélo" : vehicle.usage === "location" ? "Location" : "Prêt";

  return (
    <div className="relative" ref={ref}>
      <button
        className="flex flex-row items-center gap-1.5 text-h3 font-medium rounded-full px-3 py-1 truncate hover:opacity-80 transition-opacity"
        style={{ background: "#DBEAFE", color: "#1E40AF", maxWidth: 220 }}
        onClick={() => setOpen(!open)}
      >
        <Icon1 name={vehicle.usage === "velos" ? "tool" : "car"} size={12} color="#1E40AF" />
        <span className="truncate">{vehicle.plate}</span>
      </button>

      {open && (
        <div
          className="absolute top-full mt-2 left-0 bg-white rounded-xl shadow-lg border border-neutral-200 z-50"
          style={{ width: 260, padding: 16 }}
        >
          <div className="flex flex-col gap-3">
            <div className="flex flex-row items-center gap-3">
              <div className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 8, background: "#DBEAFE" }}>
                <Icon1 name={vehicle.usage === "velos" ? "tool" : "car"} size={18} color="#1E40AF" />
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-h2 font-semibold truncate">{vehicle.name || vehicle.plate}</span>
                <span className="text-h4 text-neutral-400">{vehicle.plate} · {usageLabel}</span>
              </div>
            </div>

            {vehicle.marque && (
              <div className="flex flex-row gap-2 text-h3 text-neutral-500">
                <span>{vehicle.marque} {vehicle.modele}</span>
                {vehicle.coloris && <span>· {vehicle.coloris}</span>}
              </div>
            )}

            {reservationId && (
              <button
                className="flex flex-row items-center justify-center gap-2 w-full py-2 rounded-lg text-h3 font-medium hover:bg-indigo-50 transition-colors"
                style={{ border: "1px solid #C7D2FE", color: "#4F46E5" }}
                onClick={() => {
                  setOpen(false);
                  navigateTo({ route: `/vehicules?reservation=${reservationId}` });
                }}
              >
                <Icon1 name="calendar" size={14} color="#4F46E5" />
                Voir la réservation
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Rental vehicle assignment modal ────────────────────────

const MOBILITY_LABELS: Record<string, string> = {
  wait: "Attente sur place",
  bike: "Vélo",
  loan: "Véhicule de prêt",
  rental: "Véhicule de location",
};

const USAGE_FOR_MOBILITY: Record<string, string> = {
  bike: "velos",
  loan: "pret",
  rental: "location",
};

function RentalVehicleModal({
  ordre,
  orgId,
  onClose,
  onAssign,
}: {
  ordre: Ordre;
  orgId: string;
  onClose: () => void;
  onAssign: (vehicle: Vehicle, reservationId: string) => void;
}) {
  const [available, setAvailable] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState<string | null>(null);
  const matchingUsage = USAGE_FOR_MOBILITY[ordre.mobility_option || ""] || null;
  const [filterByRequest, setFilterByRequest] = useState(!!matchingUsage);

  const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
  const weekLaterStr = (() => { const d = new Date(); d.setDate(d.getDate() + 7); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();

  const startDate = ordre.reception_date
    ? (() => { const d = new Date(ordre.reception_date.seconds * 1000); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })()
    : todayStr;
  const endDate = ordre.restitution
    ? (() => { const d = new Date(ordre.restitution.seconds * 1000); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })()
    : startDate || weekLaterStr;

  const startHour = ordre.reception_date
    ? new Date(ordre.reception_date.seconds * 1000).getHours() + new Date(ordre.reception_date.seconds * 1000).getMinutes() / 60
    : undefined;
  const returnHour = ordre.restitution
    ? new Date(ordre.restitution.seconds * 1000).getHours() + new Date(ordre.restitution.seconds * 1000).getMinutes() / 60
    : undefined;

  useEffect(() => {
    vehiclesApi.available({
      org_id: orgId,
      start_date: startDate,
      end_date: endDate,
      start_hour: startDate === endDate ? startHour : undefined,
      end_hour: startDate === endDate ? returnHour : undefined,
    })
      .then((v) => { console.log("[RentalModal] available vehicles:", v.length, { orgId, startDate, endDate }); setAvailable(v); })
      .catch((err) => { console.error("[RentalModal] availability error:", err); setAvailable([]); })
      .finally(() => setLoading(false));
  }, [orgId, startDate, endDate, startHour, returnHour]);

  const handleAssign = async (vehicle: Vehicle) => {
    if (!startDate || !endDate) return;
    setAssigning(vehicle.id);
    try {
      const startHour = ordre.reception_date
        ? new Date(ordre.reception_date.seconds * 1000).getHours() + new Date(ordre.reception_date.seconds * 1000).getMinutes() / 60
        : 8;
      const returnHour = ordre.restitution
        ? new Date(ordre.restitution.seconds * 1000).getHours() + new Date(ordre.restitution.seconds * 1000).getMinutes() / 60
        : 17;

      const clientName = ordre.name || ordre.contacts?.[0]?.nom || "Client";
      const reservation = await vehiclesApi.createReservation({
        org_id: orgId,
        vehicleId: vehicle.id,
        clientName,
        startDate,
        endDate,
        hasPickup: true,
        hasReturn: true,
        startHour,
        returnHour: startDate === endDate ? undefined : returnHour,
        durationHours: startDate === endDate ? (returnHour - startHour) : undefined,
        ordreId: ordre.id,
      });

      const updatedOrdre = await ordresApi.update(ordre.id, {
        rental_vehicle_id: vehicle.id,
        rental_reservation_id: reservation.id,
      });

      // Add timeline event for the booking
      ordresApi.addVehicleBookingEvent(ordre.id, {
        vehicle_plate: vehicle.plate,
        vehicle_name: vehicle.name,
        start_date: startDate,
        end_date: endDate,
      }).catch((e) => console.error("Error adding booking timeline event:", e));

      useOrdreStore.getState().updateOrdre(updatedOrdre);
      onAssign(vehicle, reservation.id);
      showNotification({ type: "success", message: `${vehicle.name} (${vehicle.plate}) attribué` });
      onClose();
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'attribution" });
    } finally {
      setAssigning(null);
    }
  };

  const usageLabel = MOBILITY_LABELS[ordre.mobility_option || ""] || "Véhicule de remplacement";
  const filteredVehicles = filterByRequest && matchingUsage
    ? available.filter((v) => v.usage === matchingUsage)
    : available;

  return (
    <BaseModal
      title="Attribuer un véhicule"
      startIcon={<Icon1 name="side-ordres" size={20} />}
      onClose={onClose}
      width="max-w-lg"
    >
      <div className="flex flex-col gap-3 p-4">
        {/* Context */}
        <div className="flex flex-col gap-1 p-3 bg-neutral-50 rounded-lg border border-neutral-200">
          <div className="flex flex-row items-center gap-2">
            <span className="text-h3 text-neutral-400">Demande client :</span>
            <span className="text-h3 font-medium text-black">{usageLabel}</span>
          </div>
          <div className="flex flex-row items-center gap-2">
            <span className="text-h3 text-neutral-400">Période :</span>
            <span className="text-h3 font-medium text-black">
              {startDate && endDate ? `${startDate} → ${endDate}` : "Dates non définies"}
            </span>
          </div>
        </div>

        {/* Filter toggle */}
        {matchingUsage && (
          <label className="flex flex-row items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filterByRequest}
              onChange={() => setFilterByRequest(!filterByRequest)}
              className="w-4 h-4 accent-[#5C6BFF]"
            />
            <span className="text-h3 text-neutral-500">
              Afficher uniquement les {usageLabel.toLowerCase()}s
            </span>
            {filterByRequest && (
              <span className="text-h4 text-neutral-400">
                ({filteredVehicles.length}/{available.length})
              </span>
            )}
          </label>
        )}

        {!startDate || !endDate ? (
          <div className="text-h3 text-center text-neutral-400 py-6">
            Définissez les dates de réception et restitution pour voir les véhicules disponibles.
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-[#5C6BFF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredVehicles.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-6">
            <span className="text-h3 text-neutral-400">
              {available.length > 0 && filterByRequest
                ? `Aucun ${usageLabel.toLowerCase()} disponible pour cette période.`
                : "Aucun véhicule disponible pour cette période."}
            </span>
            {available.length > 0 && filterByRequest && (
              <button
                className="text-h3 font-medium text-[#5C6BFF] hover:underline"
                onClick={() => setFilterByRequest(false)}
              >
                Voir tous les véhicules ({available.length})
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-1 max-h-[400px] overflow-y-auto">
            {filteredVehicles.map((v) => (
              <button
                key={v.id}
                disabled={!!assigning}
                onClick={() => handleAssign(v)}
                className="flex flex-row items-center gap-3 p-3 rounded-lg border border-neutral-200 hover:border-[#5C6BFF] hover:bg-[#F5F6FF] transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0">
                  <Icon1
                    name={v.usage === "velos" ? "tool" : "car"}
                    size={18}
                    color="#A1A1A1"
                  />
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="text-h2 font-medium text-black truncate">{v.name}</span>
                  <span className="text-h4 text-neutral-400">
                    {v.plate} · {v.marque || ""} {v.modele || ""} · {v.km} km
                  </span>
                </div>
                <span
                  className="text-h4 font-medium rounded-full px-2 py-0.5"
                  style={{
                    background: v.usage === "location" ? "#FEF3C7" : v.usage === "velos" ? "#DBEAFE" : "#D1FAE5",
                    color: v.usage === "location" ? "#92400E" : v.usage === "velos" ? "#1E40AF" : "#065F46",
                  }}
                >
                  {v.usage === "location" ? "Location" : v.usage === "velos" ? "Vélo" : "Prêt"}
                </span>
                {assigning === v.id && (
                  <div className="w-4 h-4 border-2 border-[#5C6BFF] border-t-transparent rounded-full animate-spin" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="border-t-1 p-4 flex flex-row justify-end">
        <SecondaryButton variant="small" onClick={onClose}>Fermer</SecondaryButton>
      </div>
    </BaseModal>
  );
}

// ─── Tracking preview modal (phone frame) ─────────────────

function TrackingPreviewModal({
  ordre,
  parentDossier,
  orgData,
  onClose,
}: {
  ordre: Ordre;
  parentDossier: Dossier | null;
  orgData: any;
  onClose: () => void;
}) {
  const [hoveredStar, setHoveredStar] = useState(0);
  const [selectedRating, setSelectedRating] = useState(0);

  const vehicle = ordre.vehicle || parentDossier?.vehicles?.[0];

  const formatDate = (ts?: { seconds: number }) => {
    if (!ts) return undefined;
    const d = new Date(ts.seconds * 1000);
    return d.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" });
  };

  const infos = orgData?.infos || {};
  const mockData: TrackingData = {
    ordre_num: ordre.num,
    org_name: infos.name || "",
    org_address: [infos.address, infos.postal, infos.city].filter(Boolean).join(" ") || "",
    org_phone: infos.phone_number || "",
    org_email: infos.email || "",
    tracking_step: ordre.tracking_step || "en_attente_depot",
    has_carte_grise: (parentDossier?.documents || []).some(
      (d) => d.type?.toLowerCase().includes("carte grise") || d.name?.toLowerCase().includes("carte grise")
    ),
    vehicle_plate: vehicle?.plate,
    vehicle_marque: vehicle?.marque,
    vehicle_modele: vehicle?.modele,
    vehicle_coloris: vehicle?.coloris,
    service_type: ordre.campagne || undefined,
    interventions: ordre.interventions?.map((i) => i.label),
    deposit_date: formatDate(ordre.reception_date),
    restitution_date: formatDate(ordre.restitution),
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col border rounded-[15px] overflow-hidden shadow-xl" style={{ width: 380, maxHeight: "calc(100vh - 48px)", background: "#FFFFFF", borderColor: "#D9D9D9" }}>
      {/* Header */}
      <div className="flex flex-row items-center justify-between px-[20px] py-[12px] border-b border-[#E5E5E5]">
        <div className="flex flex-col">
          <span className="text-h3 text-neutral-400">Aperçu client</span>
          <span className="text-h2 font-medium" style={{ color: "#5C6BFF" }}>
            Suivi #{ordre.num}
          </span>
        </div>
        <button onClick={onClose} className="hover:bg-neutral-100 rounded-full p-1">
          <svg width={12} height={12} viewBox="0 0 12 12" fill="none">
            <path d="M1 1L11 11M11 1L1 11" stroke="#A1A1A1" strokeWidth={1.5} strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* Scrollable content */}
      <div className="overflow-y-auto">
        <TrackingContent
          data={mockData}
          hoveredStar={hoveredStar}
          setHoveredStar={setHoveredStar}
          selectedRating={selectedRating}
          setSelectedRating={setSelectedRating}
          previewMode
        />
      </div>
    </div>
  );
}

// ─── Sign request modal ────────────────────────────────────

function SignRequestModal({
  ordreId,
  doc,
  contacts,
  onClose,
  onSuccess,
}: {
  ordreId: string;
  doc: { id: string; name: string };
  contacts: Contact[];
  onClose: () => void;
  onSuccess: (token: string, signingUrl: string) => void;
}) {
  const primaryContact = contacts.find((c) => c.is_primary) || contacts[0];
  const [signerName, setSignerName] = useState(primaryContact ? `${primaryContact.prenom || ""} ${primaryContact.nom}`.trim() : "");
  const [signerEmail, setSignerEmail] = useState(primaryContact?.email || "");
  const [sending, setSending] = useState(false);
  const [signingUrl, setSigningUrl] = useState("");

  const handleSend = async () => {
    if (!signerName.trim() || !signerEmail.trim()) return;
    setSending(true);
    try {
      const result = await ordresApi.sendSignRequest(ordreId, doc.id, signerName.trim(), signerEmail.trim());
      setSigningUrl(result.signing_url);
      onSuccess(result.token, result.signing_url);
      showNotification({ type: "success", message: `Demande de signature envoyée à ${signerEmail}` });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'envoi" });
    } finally {
      setSending(false);
    }
  };

  return (
    <BaseModal title="Envoyer pour signature" startIcon={<Icon1 name="file" size={20} />} onClose={onClose} width="max-w-md">
      <div className="flex flex-col gap-4 p-4">
        {signingUrl ? (
          <>
            <div className="flex flex-col items-center gap-3 py-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "#D1FAE5" }}>
                <Icon1 name="done2" size={24} color="#065F46" />
              </div>
              <span className="text-h2 font-semibold text-center">Demande envoyée</span>
              <span className="text-h3 text-neutral-500 text-center">
                Un email a été envoyé à {signerEmail}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-h3 font-medium text-neutral-500">Lien de signature</span>
              <div className="flex flex-row items-center gap-2">
                <input
                  readOnly
                  value={signingUrl}
                  className="flex-1 text-h3 text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2 truncate"
                />
                <button
                  className="text-h3 font-medium px-3 py-2 rounded-lg border border-neutral-200 hover:bg-neutral-50"
                  onClick={() => { navigator.clipboard.writeText(signingUrl); showNotification({ type: "success", message: "Lien copié" }); }}
                >
                  Copier
                </button>
              </div>
              <span className="text-h4 text-neutral-400">
                Partagez ce lien par WhatsApp ou tout autre canal
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-1 p-3 bg-neutral-50 rounded-lg border border-neutral-200">
              <span className="text-h4 text-neutral-400">Document</span>
              <span className="text-h3 font-medium">{doc.name}</span>
            </div>
            <TextField label="Nom du signataire" value={signerName} onChange={setSignerName} />
            <TextField label="Email du signataire" value={signerEmail} onChange={setSignerEmail} />
          </>
        )}
      </div>
      <div className="border-t p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>{signingUrl ? "Fermer" : "Annuler"}</SecondaryButton>
        {!signingUrl && (
          <Button
            variant="small"
            label={sending ? "Envoi..." : "Envoyer"}
            onClick={handleSend}
            disabled={!signerName.trim() || !signerEmail.trim() || sending}
          />
        )}
      </div>
    </BaseModal>
  );
}

// ─── Main screen ───────────────────────────────────────────

export default function OrdreDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState("fil");
  const [search, setSearch] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [showTrackingPreview, setShowTrackingPreview] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<{ doc: any; url: string } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showRentalModal, setShowRentalModal] = useState(false);
  const [rentalVehicle, setRentalVehicle] = useState<Vehicle | null>(null);
  const [signRequestDoc, setSignRequestDoc] = useState<{ id: string; name: string } | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState<"acompte" | "solde" | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const removeOrdres = useOrdreStore((s) => s.removeOrdres);
  const orgData = useDataStore((s) => s.orgData);
  const ordres = useOrdreStore((s) => s.ordres);
  const fetchAtelierData = useAtelierStore((s) => s.fetchData);
  const atelierJobs = useAtelierStore((s) => s.jobs);
  const atelierWorkers = useAtelierStore((s) => s.workers);
  const atelierCharges = useAtelierStore((s) => s.charges);

  const [parentDossier, setParentDossier] = useState<Dossier | null>(null);
  const [configuredInterventions, setConfiguredInterventions] = useState<Intervention[]>([]);

  useEffect(() => {
    if (orgData?.id) {
      fetchAtelierData(orgData.id);
      interventionsApi.list(orgData.id).then(setConfiguredInterventions).catch(() => {});
    }
  }, [orgData?.id]);

  const ordre = useMemo(() => ordres.find((o) => o.id === id), [id, ordres]);

  const fetchPayments = () => {
    if (id) paymentsApi.listForOrdre(id).then(setPayments).catch(() => {});
  };

  useEffect(() => { fetchPayments(); }, [id]);

  // Fetch parent dossier
  useEffect(() => {
    if (ordre?.dossier_id) {
      dossiersApi.get(ordre.dossier_id).then(setParentDossier).catch(() => {});
    }
  }, [ordre?.dossier_id]);

  useEffect(() => {
    if (ordre?.rental_vehicle_id) {
      vehiclesApi.get(ordre.rental_vehicle_id).then(setRentalVehicle).catch(() => {});
    } else {
      setRentalVehicle(null);
    }
  }, [ordre?.rental_vehicle_id]);

  const openDocPreview = async (doc: { id: string; gcs_path?: string }) => {
    if (!doc.gcs_path || !ordre) return;
    setLoadingPreview(true);
    try {
      const isOrdreDoc = doc.gcs_path.startsWith("ordres/");
      const url = isOrdreDoc
        ? await ordresApi.getDocumentUrl(ordre.id, doc.id)
        : parentDossier
          ? await dossiersApi.getDocumentUrl(parentDossier.id, doc.id)
          : null;
      if (url) setPreviewDoc({ doc, url });
    } catch {
      showNotification({ type: "error", message: "Impossible de charger l'aperçu" });
    } finally {
      setLoadingPreview(false);
    }
  };

  const ordreAtelierCharges = useMemo(() => {
    if (!id) return [];
    const workerMap = new Map(atelierWorkers.map((w) => [w.id, w.name]));
    const jobRows = atelierJobs
      .filter((j) => j.ordre_id === id)
      .map((j) => ({
        id: j.id,
        worker_name: workerMap.get(j.workerId) || "—",
        specialty: j.specialty,
        date: j.date,
        start_hour: j.startHour,
        duration_hours: j.durationHours,
      }));
    const chargeRows = atelierCharges
      .filter((c) => c.ordre_id === id)
      .map((c) => ({
        id: c.id,
        worker_name: "Non assigné",
        specialty: c.specialty,
        date: "—",
        start_hour: 0,
        duration_hours: c.hours,
      }));
    return [...jobRows, ...chargeRows];
  }, [atelierJobs, atelierWorkers, atelierCharges, id]);

  const assignedWorkers = useMemo(() => {
    if (!id) return [];
    const seen = new Set<string>();
    return atelierJobs
      .filter((j) => j.ordre_id === id)
      .reduce<{ name: string; specialty: string }[]>((acc, j) => {
        if (!seen.has(j.workerId)) {
          seen.add(j.workerId);
          const w = atelierWorkers.find((w) => w.id === j.workerId);
          if (w) acc.push({ name: w.name, specialty: w.specialty });
        }
        return acc;
      }, []);
  }, [atelierJobs, atelierWorkers, id]);

  if (!ordre) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: "#FAFAFA" }}>
        <span className="text-h2 text-neutral-400">Ordre introuvable</span>
      </div>
    );
  }

  const primaryVehicle = ordre.vehicle
    || (ordre.vehicle_id && parentDossier?.vehicles?.find((v) => v.id === ordre.vehicle_id))
    || null;
  const vehicleImg = primaryVehicle ? getVehicleImageUrl(primaryVehicle.marque, primaryVehicle.modele, primaryVehicle.coloris) : null;

  return (
    <div className="flex flex-row h-screen overflow-hidden" style={{ background: "#FAFAFA" }}>
      {showDeleteConfirm && (
        <ConfirmationModal
          title="Supprimer cet ordre ?"
          message="L'ordre ainsi que ses rendez-vous (réception et restitution) et ses charges atelier seront définitivement supprimés."
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={() => {
            if (id) {
              removeOrdres([id]);
              showNotification({ type: "success", message: "Ordre supprimé avec succès" });
              navigateTo({ route: "/ordres" });
            }
            setShowDeleteConfirm(false);
          }}
          confirmText="CONFIRMER"
        />
      )}
      {showEditModal && ordre && (
        <OrdreModal
          ordre={ordre}
          onClose={() => setShowEditModal(false)}
          onSave={() => setShowEditModal(false)}
        />
      )}
      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-y-auto min-w-0">
        {/* Header */}
        <div className="flex flex-row items-start justify-between px-6 pt-8 pb-4">
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <Text1>{ordre.name}</Text1>
            <span className="text-h3 text-neutral-400 font-medium">{ordre.num}</span>

            {/* Dates row + rating */}
            <div className="flex flex-row items-center gap-4 mt-1">
              {ordre.reception_date && (
                <div className="flex flex-row items-center gap-1.5">
                  <Icon1 name="horloge" size={14} color="#A1A1A1" />
                  <span className="text-h4 text-neutral-500">{formatTimestamp(ordre.reception_date)}</span>
                </div>
              )}
              {ordre.restitution && (
                <div className="flex flex-row items-center gap-1.5">
                  <Icon1 name="horloge" size={14} color="#A1A1A1" />
                  <span className="text-h4 text-neutral-500">{formatTimestamp(ordre.restitution)}</span>
                </div>
              )}
              {ordre.client_rating != null && ordre.client_rating > 0 && (
                <div className="flex flex-row items-center gap-1 px-2 py-0.5 rounded-full" style={{ background: ordre.client_rating >= 4 ? "#F0FDF4" : ordre.client_rating >= 3 ? "#FFFBEB" : "#FEF2F2" }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill={ordre.client_rating >= 4 ? "#22C55E" : ordre.client_rating >= 3 ? "#F59E0B" : "#EF4444"}>
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  <span className="text-h4 font-medium" style={{ color: ordre.client_rating >= 4 ? "#22C55E" : ordre.client_rating >= 3 ? "#F59E0B" : "#EF4444" }}>{ordre.client_rating}/5</span>
                </div>
              )}
            </div>

            {/* Status + assigned badges */}
            <div className="flex flex-row items-center gap-2 mt-1">
              <OrdreStatusDropdown
                currentStatus={ordre.status}
                onStatusChange={async (status) => {
                  try {
                    const updates: Partial<Ordre> = { status };
                    const autoTracking = STATUS_TO_TRACKING[status];
                    if (autoTracking) updates.tracking_step = autoTracking;
                    const updated = await ordresApi.update(ordre.id, updates);
                    useOrdreStore.getState().updateOrdre(updated);
                    showNotification({ type: "success", message: "Statut mis à jour" });
                  } catch {
                    showNotification({ type: "error", message: "Erreur lors de la mise à jour" });
                  }
                }}
              />
              <AssignDropdown
                assignedTo={ordre.assigned_to}
                onAssign={async (userId) => {
                  try {
                    const updated = await ordresApi.update(ordre.id, { assigned_to: userId || "" });
                    useOrdreStore.getState().updateOrdre(updated);
                    showNotification({ type: "success", message: userId ? "Assignation mise à jour" : "Assignation retirée" });
                  } catch {
                    showNotification({ type: "error", message: "Erreur lors de l'assignation" });
                  }
                }}
              />
              {assignedWorkers.map((w) => (
                <span
                  key={w.name}
                  className="flex flex-row items-center gap-1.5 text-h3 font-medium rounded-full px-3 py-1"
                  style={{
                    background: w.specialty === "carrosserie" ? "#FEF3C7" : w.specialty === "mecanique" ? "#DBEAFE" : "#F3F4F6",
                    color: w.specialty === "carrosserie" ? "#92400E" : w.specialty === "mecanique" ? "#1E40AF" : "#374151",
                  }}
                >
                  <Icon1 name="side-ordres" size={12} color={w.specialty === "carrosserie" ? "#92400E" : w.specialty === "mecanique" ? "#1E40AF" : "#374151"} />
                  {w.name}
                </span>
              ))}
              {ordre.devis_sign_status === "pending" && (
                <span className="text-h3 font-medium rounded-full px-3 py-1" style={{ background: "#DBEAFE", color: "#1E40AF" }}>
                  Devis en attente de signature
                </span>
              )}
              {ordre.devis_sign_status === "signed" && (
                <span className="text-h3 font-medium rounded-full px-3 py-1" style={{ background: "#D1FAE5", color: "#065F46" }}>
                  Devis signé
                </span>
              )}
              {ordre.amount != null && (
                <span className="text-h3 font-medium rounded-full px-3 py-1" style={{ background: "#EDE9FE", color: "#5B21B6" }}>
                  {ordre.amount.toFixed(2)} € TTC
                </span>
              )}
              {rentalVehicle && (
                <RentalVehicleBadge vehicle={rentalVehicle} reservationId={ordre.rental_reservation_id} />
              )}
            </div>

            {/* Tags */}
            {ordre.tags.length > 0 && (
              <div className="flex flex-row items-center gap-2 mt-1">
                {ordre.tags.map((tag) => {
                  const style = TAG_STYLES[tag];
                  return (
                    <span
                      key={tag}
                      className="text-h5 whitespace-nowrap"
                      style={{ background: style.bg, color: "#FFFFFF", borderRadius: 30, padding: "4px 14px", lineHeight: "18px" }}
                    >
                      {style.label}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Payment status badges */}
            {payments.length > 0 && (
              <div className="flex flex-col gap-1.5 mt-2">
                {payments.filter((p) => p.state !== "cancelled").map((p) => {
                  const stateStyles: Record<string, { bg: string; color: string; label: string }> = {
                    draft: { bg: "#F3F4F6", color: "#6B7280", label: "Brouillon" },
                    initiated: { bg: "#DBEAFE", color: "#1E40AF", label: "En attente" },
                    pending: { bg: "#FEF3C7", color: "#92400E", label: "En cours" },
                    paid: { bg: "#D1FAE5", color: "#065F46", label: "Payé" },
                    error: { bg: "#FEE2E2", color: "#991B1B", label: "Erreur" },
                  };
                  const s = stateStyles[p.state] || stateStyles.draft;
                  const reason = p.state === "error" && p.bridge?.status_reason
                    ? ` — ${p.bridge.status_reason}`
                    : "";

                  return (
                    <div key={p.id} className="flex flex-row items-center gap-2">
                      <span
                        className="text-h4 font-medium rounded-full px-3 py-0.5"
                        style={{ background: s.bg, color: s.color }}
                      >
                        {p.type === "acompte" ? "Acompte" : "Solde"} {p.amount.toFixed(2)} € — {s.label}{reason}
                      </span>
                      {(p.state === "initiated" || p.state === "error") && (
                        <button
                          className="text-h4 font-medium hover:underline"
                          style={{ color: "#3B82F6" }}
                          onClick={async () => {
                            try {
                              await paymentsApi.resend(p.id);
                              showNotification({ type: "success", message: "Lien renvoyé" });
                            } catch {
                              showNotification({ type: "error", message: "Erreur lors du renvoi" });
                            }
                          }}
                        >
                          Renvoyer
                        </button>
                      )}
                      {p.state !== "paid" && p.state !== "cancelled" && (
                        <button
                          className="text-h4 font-medium hover:underline"
                          style={{ color: "#EF4444" }}
                          onClick={async () => {
                            try {
                              await paymentsApi.cancel(p.id);
                              fetchPayments();
                              showNotification({ type: "success", message: "Paiement annulé" });
                            } catch {
                              showNotification({ type: "error", message: "Erreur lors de l'annulation" });
                            }
                          }}
                        >
                          Annuler
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

          </div>

          {/* Vehicle image + Actions */}
          <div className="flex flex-row items-start gap-4 flex-shrink-0">
            {primaryVehicle && (
              <div className="flex flex-col items-center gap-1">
                <div className="rounded-xl border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden" style={{ width: 160, height: 90 }}>
                  {vehicleImg ? (
                    <img src={vehicleImg} alt={primaryVehicle.plate} className="w-full h-full object-contain" />
                  ) : (
                    <Icon1 name="car-reception" size={32} color="#D4D4D4" />
                  )}
                </div>
                <span className="text-h4 text-neutral-500 font-medium">{primaryVehicle.plate}</span>
              </div>
            )}

            <MenuButton label="Actions">
              <div className="flex flex-col">
                <div className="flex flex-col p-1">
                  <div className="text-h2 text-neutral-400 p-2">ACTIONS RAPIDES</div>
                  <SideMenuButton
                    size="large"
                    label="Modifier l'ordre"
                    name="modify"
                    onClick={() => setShowEditModal(true)}
                  />
                  <SideMenuButton
                    size="large"
                    label="Supprimer l'ordre"
                    name="delete"
                    color="#ef4444"
                    onClick={() => setShowDeleteConfirm(true)}
                  />
                </div>
                <div className="flex flex-col border-t-1 p-1">
                  <div className="text-h2 text-neutral-400 p-2">DOSSIER</div>
                  <SideMenuButton
                    size="large"
                    label="Générer un devis"
                    name="file"
                  />
                  {ordre.devis_sign_status === "pending" ? (
                    <SideMenuButton
                      size="large"
                      label="Copier le lien de signature"
                      name="link"
                      onClick={() => {
                        if (ordre.devis_sign_token) {
                          const url = `${window.location.origin}/sign/${ordre.devis_sign_token}`;
                          navigator.clipboard.writeText(url);
                          showNotification({ type: "success", message: "Lien de signature copié" });
                        }
                      }}
                    />
                  ) : ordre.devis_sign_status === "signed" ? (
                    <SideMenuButton
                      size="large"
                      label="Devis signé"
                      name="done2"
                      color="#059669"
                    />
                  ) : null}
                  <SideMenuButton
                    size="large"
                    label="Ajouter un document"
                    name="upload"
                    onClick={() => setShowAddDoc(true)}
                  />
                </div>
                <div className="flex flex-col border-t-1 p-1">
                  <div className="text-h2 text-neutral-400 p-2">VÉHICULE DE REMPLACEMENT</div>
                  {ordre.mobility_option && ordre.mobility_option !== "wait" && !ordre.rental_vehicle_id && (
                    <div className="flex flex-row items-center gap-2 px-3 py-2 mx-1 mb-1 rounded-lg" style={{ background: "#FEF3C7", border: "1px solid #FDE68A" }}>
                      <Icon1 name="info" size={14} color="#92400E" />
                      <span className="text-h4 font-medium" style={{ color: "#92400E" }}>
                        Demande : {MOBILITY_LABELS[ordre.mobility_option] || ordre.mobility_option}
                      </span>
                    </div>
                  )}
                  {ordre.rental_vehicle_id ? (
                    <>
                      <SideMenuButton
                        size="large"
                        label={rentalVehicle ? `${rentalVehicle.name} (${rentalVehicle.plate})` : "Véhicule attribué"}
                        name="done2"
                        color="#059669"
                      />
                      <SideMenuButton
                        size="large"
                        label="Changer le véhicule"
                        name="restart"
                        onClick={async () => {
                          if (ordre.rental_reservation_id) {
                            try { await vehiclesApi.deleteReservation(ordre.rental_reservation_id); } catch {}
                          }
                          const updatedOrdre = await ordresApi.update(ordre.id, { rental_vehicle_id: "", rental_reservation_id: "" });
                          useOrdreStore.getState().updateOrdre(updatedOrdre);
                          setRentalVehicle(null);
                          setShowRentalModal(true);
                        }}
                      />
                      <SideMenuButton
                        size="large"
                        label="Retirer le véhicule"
                        name="delete"
                        color="#ef4444"
                        onClick={async () => {
                          try {
                            if (ordre.rental_reservation_id) {
                              await vehiclesApi.deleteReservation(ordre.rental_reservation_id);
                            }
                            const updatedOrdre = await ordresApi.update(ordre.id, { rental_vehicle_id: "", rental_reservation_id: "" });
                            useOrdreStore.getState().updateOrdre(updatedOrdre);
                            setRentalVehicle(null);
                            showNotification({ type: "success", message: "Véhicule de remplacement retiré" });
                          } catch {
                            showNotification({ type: "error", message: "Erreur" });
                          }
                        }}
                      />
                    </>
                  ) : (
                    <SideMenuButton
                      size="large"
                      label="Attribuer un véhicule"
                      name="car"
                      onClick={() => setShowRentalModal(true)}
                    />
                  )}
                </div>
                <div className="flex flex-col border-t-1 p-1">
                  <div className="text-h2 text-neutral-400 p-2">CLIENT</div>
                  <SideMenuButton
                    size="large"
                    label="Voir le suivi client"
                    name="eye"
                    onClick={() => setShowTrackingPreview(true)}
                  />
                  <SideMenuButton
                    size="large"
                    label="Demander un acompte"
                    name="payments"
                    onClick={() => setShowPaymentModal("acompte")}
                  />
                  <SideMenuButton
                    size="large"
                    label="Demander le solde"
                    name="payments"
                    onClick={() => setShowPaymentModal("solde")}
                  />
                  <SideMenuButton
                    size="large"
                    label="Copier le lien de suivi"
                    name="link"
                    onClick={() => {
                      const url = `${window.location.origin}/tracking/${ordre.id}`;
                      navigator.clipboard.writeText(url);
                      showNotification({ type: "success", message: "Lien copié !" });
                    }}
                  />
                </div>
                <div className="flex flex-col border-t-1 p-1">
                  <div className="text-h2 text-neutral-400 p-2">STATUT</div>
                  <MenuInception
                    size="large"
                    label="Changer le statut"
                    name="restart"
                  >
                    <div className="flex flex-col">
                      {STATUS_OPTIONS.map((s) => (
                        <SideMenuButton
                          key={s.value}
                          size="large"
                          label={s.label}
                          name={ordre.status === s.value ? "done2" : undefined}
                          onClick={async () => {
                            try {
                              const updates: Partial<Ordre> = { status: s.value };
                              const autoTracking = STATUS_TO_TRACKING[s.value];
                              if (autoTracking) updates.tracking_step = autoTracking;
                              const updated = await ordresApi.update(ordre.id, updates);
                              useOrdreStore.getState().updateOrdre(updated);
                              showNotification({ type: "success", message: "Statut mis à jour" });
                            } catch {
                              showNotification({ type: "error", message: "Erreur lors de la mise à jour" });
                            }
                          }}
                        />
                      ))}
                    </div>
                  </MenuInception>
                  <MenuInception
                    size="large"
                    label="Suivi client"
                    name="restart"
                  >
                    <div className="flex flex-col">
                      {TRACKING_STEP_OPTIONS.map((t) => (
                        <SideMenuButton
                          key={t.value}
                          size="large"
                          label={t.label}
                          name={ordre.tracking_step === t.value ? "done2" : undefined}
                          onClick={async () => {
                            try {
                              const updated = await ordresApi.update(ordre.id, { tracking_step: t.value });
                              useOrdreStore.getState().updateOrdre(updated);
                              showNotification({ type: "success", message: "Suivi client mis à jour" });
                            } catch {
                              showNotification({ type: "error", message: "Erreur lors de la mise à jour" });
                            }
                          }}
                        />
                      ))}
                    </div>
                  </MenuInception>
                </div>
              </div>
            </MenuButton>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 overflow-hidden">
          <ChunkyTabs activeTab={activeTab} tabs={CONTENT_TABS} setActiveTab={setActiveTab} />
        </div>

        {/* Search */}
        <div className="px-6 pt-3" style={{ maxWidth: 500 }}>
          <SearchField value={search} onChange={setSearch} placeholder="Chercher .." />
        </div>

        {/* Tab content */}
        <div className="px-6 pt-4 pb-8 flex-1 overflow-x-hidden">
          {activeTab === "fil" && <FilTab timeline={ordre.timeline || []} search={search} documents={ordre.documents} ordreId={ordre.id} />}
          {activeTab === "contacts" && <ContactsTab contacts={ordre.contacts?.length ? ordre.contacts : parentDossier?.contacts || []} ordreId={ordre.id} />}
          {activeTab === "interventions" && <InterventionsTab interventions={ordre.interventions} configuredInterventions={configuredInterventions} />}
          {activeTab === "atelier" && <AtelierTab charges={ordreAtelierCharges} />}
          {activeTab === "vehicules" && (
              <VehiculesTab
                dossierVehicles={parentDossier?.vehicles || (ordre.vehicle ? [ordre.vehicle] : [])}
                selectedVehicleId={ordre.vehicle_id || ordre.vehicle?.id}
                onSelectVehicle={async (vehicleId) => {
                  try {
                    const updated = await ordresApi.update(ordre.id, { vehicle_id: vehicleId } as any);
                    useOrdreStore.getState().updateOrdre({
                      ...updated,
                      vehicle: (parentDossier?.vehicles || []).find((v) => v.id === vehicleId),
                    });
                    showNotification({ type: "success", message: "Véhicule sélectionné" });
                  } catch {
                    showNotification({ type: "error", message: "Erreur lors de la sélection" });
                  }
                }}
              />
            )}
          {activeTab === "documents" && (
              <DocumentsTab
                documents={(() => {
                  const dossierDocs = parentDossier?.documents || [];
                  const ordreDocs = ordre.documents || [];
                  const seen = new Set<string>();
                  const merged: typeof dossierDocs = [];
                  for (const d of [...ordreDocs, ...dossierDocs]) {
                    if (!seen.has(d.id)) { seen.add(d.id); merged.push(d); }
                  }
                  return merged;
                })()}
                dossierId={ordre.dossier_id}
                ordreId={ordre.id}
                onPreview={openDocPreview}
                onAddDoc={() => setShowAddDoc(true)}
                onSignRequest={(doc) => setSignRequestDoc(doc)}
                devisSignStatus={ordre.devis_sign_status}
                devisDocumentId={ordre.devis_document_id}
              />
            )}
        </div>
      </div>

      {/* Right chat panel — full on xl screens */}
      <div className="flex-shrink-0 hidden 2xl:block" style={{ width: 400 }}>
        <ChatPanel
          discussionId={parentDossier?.discussion_id || ordre.discussion_id}
          internalDiscussionId={parentDossier?.internal_discussion_id || ordre.internal_discussion_id}
          entityType="ordre"
          entityId={ordre.id}
          entityName={ordre.name}
          members={[{ name: "M. Martin" }, { name: "Emeric" }, { name: "Mélanie" }]}
          onlineCount={2}
        />
      </div>

      {/* Floating chat widget — below xl only */}
      <FloatingChatWidget
        discussionId={parentDossier?.discussion_id || ordre.discussion_id}
        internalDiscussionId={parentDossier?.internal_discussion_id || ordre.internal_discussion_id}
        entityType="ordre"
        entityId={ordre.id}
        entityName={ordre.name}
      />

      {/* Add document modal — uploads to parent dossier */}
      {showAddDoc && ordre.dossier_id && (
        <AddDocumentModalOrdre
          dossierId={ordre.dossier_id}
          onClose={() => setShowAddDoc(false)}
          onSuccess={(updated) => { setParentDossier(updated); setShowAddDoc(false); }}
        />
      )}

      {/* Document preview modal */}
      {previewDoc && (
        <DocumentPreviewModal
          doc={previewDoc.doc}
          url={previewDoc.url}
          onClose={() => setPreviewDoc(null)}
          onDownload={() => window.open(previewDoc.url, "_blank")}
        />
      )}

      {/* Tracking preview modal (phone frame) */}
      {showTrackingPreview && ordre && (
        <TrackingPreviewModal
          ordre={ordre}
          parentDossier={parentDossier}
          orgData={orgData}
          onClose={() => setShowTrackingPreview(false)}
        />
      )}
      {showRentalModal && ordre && orgData?.id && (
        <RentalVehicleModal
          ordre={ordre}
          orgId={orgData.id}
          onClose={() => setShowRentalModal(false)}
          onAssign={(v, resId) => setRentalVehicle(v)}
        />
      )}
      {signRequestDoc && ordre && (
        <SignRequestModal
          ordreId={ordre.id}
          doc={signRequestDoc}
          contacts={ordre.contacts?.length ? ordre.contacts : parentDossier?.contacts || []}
          onClose={() => setSignRequestDoc(null)}
          onSuccess={(token, url) => {
            // Refresh the ordre to get updated sign status
            ordresApi.get(ordre.id).then((updated) => {
              useOrdreStore.getState().updateOrdre(updated);
            }).catch(() => {});
          }}
        />
      )}
      {showPaymentModal && ordre && (
        <PaymentRequestModal
          ordre={ordre}
          defaultType={showPaymentModal}
          onClose={() => setShowPaymentModal(null)}
          onSuccess={() => {
            fetchPayments();
            ordresApi.get(ordre.id).then((updated) => {
              useOrdreStore.getState().updateOrdre(updated);
            }).catch(() => {});
          }}
        />
      )}
    </div>
  );
}
